import React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

const ContactFormCT = ({ formId = 'heroForm', isModal = false, onCloseModal }) => {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitSuccessful },
  } = useForm({
    defaultValues: {
      name: '',
      company: '',
      email: '',
      phone: '',
    }
  });

  const onSubmit = (data) => {
    console.log(`Corporate Tax form (${formId}) submit:`, data);
    if (onCloseModal) onCloseModal();
    navigate('/thank-you');
  };

  const idPrefix = formId === 'heroForm' ? 'hf' : 'mf';

  if (isSubmitSuccessful) {
    return (
      <div className="form-ok" style={{ display: 'flex' }} role="status" aria-live="polite">
        <svg width="52" height="52" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="11" fill="#e8faf0" />
          <path d="M7 12.4l3.2 3.2L17 8.8" stroke="#0f7a3d" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <h3>Request received</h3>
        <p>
          A senior tax accountant will call you on the number you gave, within one business day.
          If it is urgent, message us on{' '}
          <a href="https://wa.me/971542119784" target="_blank" rel="noopener noreferrer">
            WhatsApp
          </a>.
        </p>
      </div>
    );
  }

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className="form-live">
        <div className={`field ${errors.name ? 'invalid' : ''}`}>
          <label htmlFor={`${idPrefix}-name`}>
            Full name <span className="req" aria-hidden="true">*</span>
          </label>
          <input
            id={`${idPrefix}-name`}
            type="text"
            autoComplete="name"
            placeholder="Ahmed Al Mansouri"
            {...register('name', { required: 'Please enter your name' })}
          />
          {errors.name && (
            <p className="err" id={`${idPrefix}-name-err`} role="alert" style={{ display: 'flex' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 2 1 21h22L12 2Zm1 14h-2v2h2v-2Zm0-7h-2v5h2V9Z" />
              </svg>
              <span>{errors.name.message}</span>
            </p>
          )}
        </div>

        <div className={`field ${errors.company ? 'invalid' : ''}`}>
          <label htmlFor={`${idPrefix}-company`}>
            Company name <span className="req" aria-hidden="true">*</span>
          </label>
          <input
            id={`${idPrefix}-company`}
            type="text"
            autoComplete="organization"
            placeholder="As shown on your trade licence"
            {...register('company', { required: 'Please enter your company name' })}
          />
          {errors.company && (
            <p className="err" id={`${idPrefix}-company-err`} role="alert" style={{ display: 'flex' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 2 1 21h22L12 2Zm1 14h-2v2h2v-2Zm0-7h-2v5h2V9Z" />
              </svg>
              <span>{errors.company.message}</span>
            </p>
          )}
        </div>

        <div className={`field ${errors.email ? 'invalid' : ''}`}>
          <label htmlFor={`${idPrefix}-email`}>
            Work email <span className="req" aria-hidden="true">*</span>
          </label>
          <input
            id={`${idPrefix}-email`}
            type="email"
            autoComplete="email"
            placeholder="you@company.ae"
            {...register('email', {
              required: 'Please enter a valid email address',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
                message: 'Please enter a valid email address',
              },
            })}
          />
          {errors.email && (
            <p className="err" id={`${idPrefix}-email-err`} role="alert" style={{ display: 'flex' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 2 1 21h22L12 2Zm1 14h-2v2h2v-2Zm0-7h-2v5h2V9Z" />
              </svg>
              <span>{errors.email.message}</span>
            </p>
          )}
        </div>

        <div className={`field ${errors.phone ? 'invalid' : ''}`}>
          <label htmlFor={`${idPrefix}-phone`}>
            Mobile or WhatsApp <span className="req" aria-hidden="true">*</span>
          </label>
          <input
            id={`${idPrefix}-phone`}
            type="tel"
            autoComplete="tel"
            placeholder="+971 5X XXX XXXX"
            {...register('phone', {
              required: 'Please enter a phone number with at least 7 digits',
              validate: (v) => v.replace(/\D/g, '').length >= 7 || 'Please enter a phone number with at least 7 digits',
            })}
          />
          {!isModal && (
            <p className="hint" id={`${idPrefix}-phone-hint`}>
              UAE mobile preferred so we can reach you on WhatsApp.
            </p>
          )}
          {errors.phone && (
            <p className="err" id={`${idPrefix}-phone-err`} role="alert" style={{ display: 'flex' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 2 1 21h22L12 2Zm1 14h-2v2h2v-2Zm0-7h-2v5h2V9Z" />
              </svg>
              <span>{errors.phone.message}</span>
            </p>
          )}
        </div>

        <button className="btn btn--lg btn--block" type="submit">
          Book a free review
        </button>
        <p className="form-note">
          No cost and no obligation. We reply within one business day.
          <br />
          By sending this you agree we may contact you about your Corporate Tax filing. We do not sell or share your details.{' '}
          <a href="#privacy">How we handle your data</a>.
        </p>

        <div className="form-trust">
          <span className="stars" aria-hidden="true">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="#cd9830">
              <path d="m12 17.3-6.2 3.7 1.7-7L2 9.2l7.2-.6L12 2l2.8 6.6 7.2.6-5.5 4.8 1.7 7z" />
            </svg>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="#cd9830">
              <path d="m12 17.3-6.2 3.7 1.7-7L2 9.2l7.2-.6L12 2l2.8 6.6 7.2.6-5.5 4.8 1.7 7z" />
            </svg>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="#cd9830">
              <path d="m12 17.3-6.2 3.7 1.7-7L2 9.2l7.2-.6L12 2l2.8 6.6 7.2.6-5.5 4.8 1.7 7z" />
            </svg>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="#cd9830">
              <path d="m12 17.3-6.2 3.7 1.7-7L2 9.2l7.2-.6L12 2l2.8 6.6 7.2.6-5.5 4.8 1.7 7z" />
            </svg>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="#cd9830">
              <path d="m12 17.3-6.2 3.7 1.7-7L2 9.2l7.2-.6L12 2l2.8 6.6 7.2.6-5.5 4.8 1.7 7z" />
            </svg>
          </span>
          <span>
            <b>4.9 average rating on Google.</b> Separately, 500+ UAE businesses served since 2000.
          </span>
        </div>
      </div>
    </form>
  );
};

export default ContactFormCT;
